import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiUpload, FiX, FiFile, FiEye, FiTrash2, FiLoader } from 'react-icons/fi';
import taskService from '../../services/taskService';
import './TaskDetail.css';

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);


  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  useEffect(() => {
    fetchTask();
    fetchComments();
  }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const data = await taskService.getTaskById(id);
      setTask(data.task);
      setAttachments(data.task.attachments);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch task');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const data = await taskService.getComments(id);
      setComments(data.comments);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await taskService.deleteTask(id);
      navigate('/app/tasks');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete task');
    }
  };

  // end image start
  const getFileIcon = (type) => {
    if (type?.startsWith('image/'));
    if (type === 'application/pdf');
    if (type?.includes('word'));
    if (type?.includes('excel') || type?.includes('spreadsheet'));
    if (type?.includes('zip') || type?.includes('rar'));
    return <FiFile size={24} style={{ color: 'var(--color-text-secondary, #9ca3af)' }} />;
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handlePreview = (url) => {
    console.log(url);
    setPreviewImage(url);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewImage(null);
  };

  // end image preview

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await taskService.addComment(id, newComment);
      setNewComment('');
      fetchComments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add comment');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'To Do': 'badge-secondary',
      'In Progress': 'badge-primary',
      'In Review': 'badge-warning',
      'Done': 'badge-success',
      'Blocked': 'badge-danger'
    };
    return statusColors[status] || 'badge-secondary';
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Low': 'badge-info',
      'Medium': 'badge-warning',
      'High': 'badge-danger',
      'Critical': 'badge-danger-dark'
    };
    return priorityColors[priority] || 'badge-secondary';
  };

  if (loading) {
    return <div className="loading">Loading task...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
        <Link to="/app/tasks" className="btn btn-primary">Back to Tasks</Link>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="error-container">
        <div className="alert alert-warning">Task not found</div>
        <Link to="/app/tasks" className="btn btn-primary">Back to Tasks</Link>
      </div>
    );
  }

  return (
    <div className="task-detail-container">
      <div className="task-detail-header">
        <div className="header-left">
          <Link to="/app/tasks" className="back-link">
            <i className="fas fa-arrow-left"></i> Back to Tasks
          </Link>
          <h1>{task.title}</h1>
        </div>
        <div className="header-actions">
          <Link to={`/app/tasks/${id}/edit`} className="btn btn-outline-primary">
            <i className="fas fa-edit"></i> Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-outline-danger">
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>

      <div className="task-detail-content">
        {/* <div className="task-info-section">
          <div className="info-grid">
            <div className="info-item">
              <label>Status</label>
              <span className={`badge ${getStatusBadge(task.status)}`}>
                {task.status}
              </span>
            </div>

            <div className="info-item">
              <label>Priority</label>
              <span className={`badge ${getPriorityBadge(task.priority)}`}>
                {task.priority}
              </span>
            </div>

            <div className="info-item">
              <label>Project</label>
              {task.project ? (
                <Link to={`/app/projects/${task.project.id}`}>
                  {task.project.name}
                </Link>
              ) : (
                <span className="text-muted">No project</span>
              )}
            </div>

            <div className="info-item">
              <label>Assignee</label>
              {task.assignee ? (
                <span>
                  {task.assignee.first_name} {task.assignee.last_name}
                </span>
              ) : (
                <span className="text-muted">Unassigned</span>
              )}
            </div>

            <div className="info-item">
              <label>Start Date</label>
              <span>
                {task.start_date ? new Date(task.start_date).toLocaleDateString() : '-'}
              </span>
            </div>

            <div className="info-item">
              <label>Due Date</label>
              <span>
                {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
              </span>
            </div>

            <div className="info-item">
              <label>Estimated Hours</label>
              <span>{task.estimated_hours || '-'}</span>
            </div>

            <div className="info-item">
              <label>Actual Hours</label>
              <span>{task.actual_hours || '-'}</span>
            </div>

            <div className="info-item">
              <label>Created By</label>
              {task.created_by_user ? (
                <span>
                  {task.created_by_user.first_name} {task.created_by_user.last_name}
                </span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </div>

            <div className="info-item">
              <label>Created At</label>
              <span>{new Date(task.createdAt).toLocaleString()}</span>
            </div>
          </div>

          {task.description && (
            <div className="description-section">
              <h3>Description</h3>
              <p>{task.description}</p>
            </div>
          )}

          {task.tags && (
            <div className="tags-section">
              <h3>Tags</h3>
              <div className="tags">
                {task.tags.split(',').map((tag, index) => (
                  <span key={index} className="badge badge-light">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div> */}

        <div >
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Details
              </h2>
            </div>

            {/* Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 p-6">

              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`badge ${getStatusBadge(task.status)}`}>
                  {task.status}
                </span>
              </div>



              <div className="flex justify-between">
                <span className="text-gray-500">Priority</span>
                <span className={`badge ${getPriorityBadge(task.priority)}`}>
                  {task.priority}
                </span>
              </div>


              <div className="flex justify-between">
                <span className="text-gray-500">Project</span>
                <span className="font-medium">{task.project.name}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Assignee</span>
                <span className="font-medium">{task?.assignee?.first_name} {task?.assignee?.last_name}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Start Date</span>
                <span>{task.start_date}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Due Date</span>
                <span>{task.due_date}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Estimated Hours</span>
                <span>{task.estimated_hours}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Actual Hours</span>
                <span>{task.actual_hours}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-500">Created At</span>
                <span>{formatDate(task.createdAt)}</span>
              </div>

            </div>

            {/* Description */}
            <div className="border-t border-gray-200 px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Description
              </h3>

              <div
                className="prose max-w-none text-gray-600"
                dangerouslySetInnerHTML={{ __html: task.description }}
              />

            </div>

            <div className="border-t border-gray-200 px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Acceptance Criteria
              </h3>
              <div
                className="prose max-w-none text-gray-600"
                dangerouslySetInnerHTML={{ __html: task.acceptance_criteria }}
              />
            </div>
            <div className="border-t border-gray-200 px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Discussion
              </h3>
              <div
                className="prose max-w-none text-gray-600"
                dangerouslySetInnerHTML={{ __html: task.discussion }}
              />
            </div>

            {/* Tags */}

            <div className="border-t border-gray-200 px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Tags
              </h3>
              {task.tags && (
                <div className="flex flex-wrap gap-2">
                  <div className="tags">
                    {task.tags.split(',').map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className='px-6 py-5'>
              {attachments.length > 0 ? (
                <>
                  <ul className="tf-attachment-list">
                    {attachments.map((attachment) => (
                      <li key={attachment.id} className="tf-attachment-item">
                        <span className="tf-attachment-file-icon">
                          {getFileIcon(attachment.file_type)}
                        </span>

                        <div className="tf-attachment-info">
                          <span className="tf-attachment-name">
                            {attachment.file_name}
                          </span>
                          <span className="tf-attachment-meta">
                            {formatBytes(attachment.file_size)}
                          </span>
                        </div>

                        <div className="tf-attachment-actions">
                          <button
                            type="button"
                            className="tf-attachment-preview"
                            onClick={() => handlePreview(attachment?.attachment_url)}
                            title="Preview"
                          >
                            <FiEye size={16} />
                          </button>

                          <button
                            type="button"
                            className="tf-attachment-remove"
                            // onClick={() => removeAttachment(attachment.id)}
                            title="Delete"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="tf-empty-msg"></p>
              )}
            </div>

            {showPreview && (
              <div className="tf-preview-overlay position-relative" onClick={closePreview}>
                <div
                  className="tf-preview-modal min-w-[17rem] min-h-[17rem]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="tf-preview-close text-gray-500 hover:text-red-700 position-absolute top-1 right-1"
                    onClick={closePreview}
                  >
                    <FiX size={22} />
                  </button>

                  <img
                    src={previewImage}
                    alt="Attachment Preview"
                    className="tf-preview-image"
                  />
                </div>
              </div>
            )}

          </div>
        </div>

        <div className="comments-section">
          <h3>Comments ({comments.length})</h3>

          <form onSubmit={handleAddComment} className="comment-form">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="form-control"
              rows="3"
            />
            <button type="submit" className="btn btn-primary">
              Add Comment
            </button>
          </form>

          <div className="comments-list">
            {comments.map((comment) => (
              <div className="timeline-item" key={comment.id}>


                <div className="timeline-dot"></div>

                {/* Comment Card */}
                <div className="comment">
                  <div className="timeline-avatar">
                    {comment.user?.avatar_url ? (
                      <img
                        src={comment.user.avatar_url}
                        alt=""
                        className="avatar"
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {comment.user?.first_name?.charAt(0)}
                      </div>
                    )}

                    <span className="author-name">
                      {comment.user?.first_name} {comment.user?.last_name}
                    </span>
                  </div>
                  <div className="comment-body">
                    <span className="comment-date">
                      {new Date(comment.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <div className="comment-body">
                    {comment.comment}
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;


